import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PersonOutline,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { endUserApiClient } from '../utils/api';

const isDevelopment = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_SIMPLE_LOGIN === 'true';

const EndUserLogin: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { roomId } = useParams();

  const normalizePhoneNumber = (value: string): string => {
    return value.replace(/[^\d]/g, '');
  };

  const formatPhoneNumber = (value: string): string => {
    const normalized = normalizePhoneNumber(value);
    if (normalized.length <= 3) return normalized;
    if (normalized.length <= 7) return `${normalized.slice(0, 3)}-${normalized.slice(3)}`;
    return `${normalized.slice(0, 3)}-${normalized.slice(3, 7)}-${normalized.slice(7, 11)}`;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const normalized = normalizePhoneNumber(value);
    
    if (normalized.length <= 11) {
      setPhoneNumber(formatPhoneNumber(value));
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!phoneNumber.trim() || !displayName.trim()) {
        setError('電話番号とお名前を入力してください');
        return;
      }

      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      
      if (normalizedPhone.length !== 11 || !normalizedPhone.startsWith('0')) {
        setError('正しい携帯電話番号を入力してください（例: 09012345678）');
        return;
      }

      const response = await endUserApiClient.loginWithPhone(normalizedPhone, displayName.trim());
      
      if (response.success) {
        if (roomId) {
          navigate(`/user/chat/${roomId}`);
        } else {
          navigate('/user/chat/demo');
        }
      } else {
        setError(response.message || 'ログインに失敗しました');
      }
    } catch (err) {
      setError('ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 本番環境では簡易ログインを無効化
  if (!isDevelopment) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 500 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src="/logo-vertical.png"
                  alt="MyTalk SYSTEM"
                  sx={{
                    width: '150px',
                    height: 'auto',
                    mb: 2,
                    mx: 'auto',
                  }}
                />
                <Typography variant="h5" component="h1" gutterBottom fontWeight={600}>
                  MyTalk SYSTEM
                </Typography>
                <Box sx={{ mt: 4, mb: 2 }}>
                  <BlockIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  このページは現在ご利用いただけません。
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  チャットルームへはメールに記載されたリンクからアクセスしてください。
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/logo-vertical.png"
                alt="MyTalk SYSTEM"
                sx={{
                  width: '150px',
                  height: 'auto',
                  mb: 2,
                  mx: 'auto',
                }}
              />
              <Typography variant="h5" component="h1" gutterBottom fontWeight={600}>
                MyTalk SYSTEM
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                マイトークシステム
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                お客様情報を入力してください
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="携帯電話番号"
                variant="outlined"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                required
                placeholder="09012345678（ハイフンなしでも可）"
                helperText="ハイフンは自動で挿入されます"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                label="お名前"
                variant="outlined"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                placeholder="山田太郎"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
              >
                {loading ? 'ログイン中...' : 'チャットルームに参加'}
              </Button>
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>ご注意:</strong><br />
                こちらは簡易版です。実際のサービスではSMS認証が行われます。
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default EndUserLogin; 